import { CheckCircleIcon, ExclamationTriangleIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { ReferentMutation } from '../../../@types/Company';
import type { ReferentSmtpConfigData, SmtpFormData } from '../../../@types/MailConfig';
import { validateCompanyStep } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import { updateSetupState } from '../../common/SetupPage/SetupPage.module';
import Spinner from '../../common/Spinner';
import SmtpConfigCard from './SmtpConfigCard';

export default function MailConfigSetup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const company = useSelector((s: RootState) => s.auth.company);
  const jwt = useSelector((s: RootState) => s.auth.token);

  const [initialLoading, setInitialLoading] = useState(true);
  const [smtpConfigs, setSmtpConfigs] = useState<Record<string, ReferentSmtpConfigData>>({});

  const brandingReferents: ReferentMutation[] = (company?.brandingSettings?.referents ?? [])
    .filter((r) => r.title && r.firstname && r.lastname);

  const fetchSmtpConfigs = useCallback(async(isInitial = false) => {
    if (!company?.id || !jwt) { return; }
    if (isInitial) { setInitialLoading(true); }
    try {
      const res = await fetch(`/api/companies/${company.id}/mail-config/referents`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!res.ok) { throw new Error('Fetch failed'); }
      const json = await res.json();
      setSmtpConfigs(json.smtpConfigs ?? {});
    } catch (e) {
      console.error(e);
      showToast('error', 'Erreur lors du chargement de la configuration mail.');
    } finally {
      if (isInitial) { setInitialLoading(false); }
    }
  }, [company?.id, jwt]);

  useEffect(() => { fetchSmtpConfigs(true); }, [fetchSmtpConfigs]);

  const handleSmtpSave = useCallback(async(referentId: string, formData: SmtpFormData) => {
    if (!company?.id || !jwt) { return; }
    try {
      const encodedReferentId = encodeURIComponent(referentId);
      const res = await fetch(`/api/companies/${company.id}/mail-config/smtp/${encodedReferentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify(formData),
      });
      if (!res.ok) { throw new Error('SMTP save failed'); }
      showToast('success', 'Configuration SMTP enregistrée.');
      await fetchSmtpConfigs();
    } catch (e) {
      console.error(e);
      showToast('error', 'Erreur lors de la sauvegarde SMTP.');
    }
  }, [company?.id, jwt, fetchSmtpConfigs]);

  const handleSmtpTest = useCallback(async(referentId: string): Promise<{ success: boolean; message: string }> => {
    if (!company?.id || !jwt) { return { success: false, message: 'Erreur interne' }; }
    try {
      const encodedReferentId = encodeURIComponent(referentId);
      const res = await fetch(`/api/companies/${company.id}/mail-config/smtp/${encodedReferentId}/test`, {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      const result = await res.json();
      if (result.success) {
        await fetchSmtpConfigs();
      }
      return result;
    } catch (e) {
      console.error(e);
      return { success: false, message: 'Erreur de connexion au serveur.' };
    }
  }, [company?.id, jwt, fetchSmtpConfigs]);

  const allVerified = brandingReferents.length > 0
    && brandingReferents.every((r) => smtpConfigs[r.id]?.isVerified === true);

  const handleValidateStep = useCallback(async() => {
    if (!company || !jwt) { return; }
    await updateSetupState(company, jwt, 'mailConfigSetup', dispatch);
    dispatch(validateCompanyStep('mailConfigSetup'));
    showToast('success', 'Configuration mail validée !');
    navigate(ROUTES.SETUP_SIMPLIFIED_DASHBOARD);
  }, [company, jwt, dispatch, navigate]);

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center rounded-lg bg-white p-16 shadow-md">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section A: Referent Summary */}
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6 border-b border-gray-200 pb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Vos référents publics
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Chaque référent public défini dans votre image de marque doit disposer d'une configuration SMTP vérifiée
            pour pouvoir envoyer des mails depuis son adresse.
          </p>
        </div>

        {brandingReferents.length === 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
            <ExclamationTriangleIcon className="mx-auto mb-2 h-8 w-8 text-amber-500" />
            <p className="font-medium text-amber-800">Aucun référent public configuré</p>
            <p className="mt-1 text-sm text-amber-600">
              Ajoutez au moins un référent dans l'étape "Personnalisation de l'image de marque".
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {brandingReferents.map((referent) => {
              const cfg = smtpConfigs[referent.id];
              const isVerified = cfg?.isVerified === true;
              const isConfigured = Boolean(cfg);

              return (
                <div
                  key={referent.id}
                  className={`rounded-xl border-2 p-4 transition-colors ${
                    isVerified
                      ? 'border-emerald-200 bg-emerald-50/30'
                      : isConfigured
                        ? 'border-amber-200 bg-amber-50/30'
                        : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {referent.photoName ? (
                      <img
                        src={referent.photoName}
                        alt={`${referent.firstname} ${referent.lastname}`}
                        className="h-12 w-12 rounded-full border-2 border-white object-cover shadow"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-blue-600 text-sm font-bold text-white shadow">
                        {referent.firstname?.[0]}{referent.lastname?.[0]}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-900">
                        {referent.title} {referent.firstname} {referent.lastname}
                      </p>
                      {referent.email && (
                        <p className="truncate text-xs text-gray-500">{referent.email}</p>
                      )}
                      {referent.userId ? (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-blue-600">
                          <UserCircleIcon className="h-3.5 w-3.5" />
                          Utilisateur lié
                        </p>
                      ) : (
                        <p className="mt-0.5 text-xs text-amber-500">Aucun utilisateur lié</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-right">
                    {isVerified ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                        <CheckCircleIcon className="h-3.5 w-3.5" /> SMTP vérifié
                      </span>
                    ) : isConfigured ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Non vérifié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                        Non configuré
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section B: SMTP Config per Referent */}
      {brandingReferents.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="mb-6 border-b border-gray-200 pb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Configuration SMTP par référent
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Configurez les paramètres d'envoi pour chaque référent public.
              Chaque référent doit avoir une configuration SMTP vérifiée pour pouvoir envoyer des mails depuis son adresse.
            </p>
          </div>

          <div className="space-y-5">
            {brandingReferents.map((referent) => (
              <SmtpConfigCard
                key={referent.id}
                referent={referent}
                config={smtpConfigs[referent.id]}
                onSave={handleSmtpSave}
                onTest={handleSmtpTest}
              />
            ))}
          </div>
        </div>
      )}

      {/* Validation */}
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {allVerified ? (
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircleIcon className="h-6 w-6" />
                <span className="font-semibold">Toutes les conditions sont remplies</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-700">
                <ExclamationTriangleIcon className="h-6 w-6" />
                <span className="font-semibold">Configuration incomplète</span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleValidateStep}
            disabled={!allVerified}
            className="rounded-lg bg-emerald-600 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Valider la configuration mail
          </button>
        </div>

        {!allVerified && (
          <div className="mt-4 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <p className="font-medium">Pour valider cette étape, vous devez :</p>
            <ul className="list-inside list-disc space-y-1">
              {brandingReferents.length === 0 && (
                <li>Définir au moins un référent public dans l'image de marque</li>
              )}
              {brandingReferents.some((r) => !smtpConfigs[r.id]?.isVerified) && (
                <li>Configurer et vérifier le SMTP de tous les référents publics</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
